import React, { useState } from 'react';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import { CaregiverFormData } from '../types';
import DocumentViewerDialog from '../../../components/DocumentViewerDialog';
import FileUpload from '../../../components/FileUpload';

interface GeneratedDoc {
  id: number;
  documentName: string;
  createdAt: string;
}

interface Props {
  formData: CaregiverFormData;
  isGeneratingDocs?: boolean;
  generatedDocs?: GeneratedDoc[];
  docGenError?: string | null;
}

const FieldDisplay: React.FC<{ label: string; value: string | boolean }> = ({ label, value }) => {
  if (!value && value !== false) return null;
  const display =
    typeof value === 'boolean'
      ? value
        ? 'Yes'
        : 'No'
      : value.includes(';')
        ? undefined
        : value;

  return (
    <Grid2 size={{ xs: 12, sm: 6 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {typeof value === 'string' && value.includes(';') ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {value.split(';').filter(Boolean).map((v) => (
            <Chip key={v} label={v} size="small" />
          ))}
        </Box>
      ) : (
        <Typography variant="body2">{display}</Typography>
      )}
    </Grid2>
  );
};

const SubmittedStep: React.FC<Props> = ({ formData, isGeneratingDocs, generatedDocs = [], docGenError }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [viewingDoc, setViewingDoc] = useState<GeneratedDoc | null>(null);

  const handleAccordion = (panel: string) => (_: unknown, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <>
      <Box textAlign="center" sx={{ mb: 3 }}>
        <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50', mb: 1 }} />
        <Typography variant="h5" gutterBottom>
          {t('form.submittedTitle', 'Application Submitted!')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('form.submittedMessage', 'Your application has been submitted successfully. You can review your information below.')}
        </Typography>
      </Box>

      {/* File Upload */}
      <Box sx={{ mb: 3 }}>
        <FileUpload
          formData={formData as unknown as Record<string, any>}
          formName={formData.type || 'HHA'}
          layout="stacked"
          themeName="validation"
        />
      </Box>

      {/* Demographics */}
      <Accordion expanded={expanded === 'demographics'} onChange={handleAccordion('demographics')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>{t('form.demographics', 'Demographics')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid2 container spacing={1}>
            <FieldDisplay label={t('form.firstName', 'First Name')} value={formData.firstName} />
            <FieldDisplay label={t('form.middleName', 'Middle Name')} value={formData.middleName} />
            <FieldDisplay label={t('form.lastName', 'Last Name')} value={formData.lastName} />
            <FieldDisplay label={t('form.dateOfBirth', 'Date of Birth')} value={formData.dateOfBirth} />
            <FieldDisplay label={t('form.email', 'Email')} value={formData.email} />
            <FieldDisplay label={t('form.phone', 'Phone')} value={formData.phone} />
            <FieldDisplay label={t('form.gender', 'Gender')} value={formData.gender} />
            <FieldDisplay label={t('form.street', 'Street Address')} value={formData.street} />
            <FieldDisplay label={t('form.city', 'City')} value={formData.city} />
            <FieldDisplay label={t('form.state', 'State')} value={formData.state} />
            <FieldDisplay label={t('form.zipCode', 'Zip Code')} value={formData.zipCode} />
            <FieldDisplay label={t('form.countyName', 'County')} value={formData.countyName} />
            <FieldDisplay label={t('form.primaryLanguage', 'Primary Language')} value={formData.primaryLanguage} />
            <FieldDisplay label={t('form.additionalLanguages', 'Additional Languages')} value={formData.additionalLanguages} />
          </Grid2>
        </AccordionDetails>
      </Accordion>

      {/* Human Resources */}
      <Accordion expanded={expanded === 'hr'} onChange={handleAccordion('hr')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>{t('form.humanResources', 'Human Resources')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid2 container spacing={1}>
            <FieldDisplay label={t('form.taxClassification', 'Tax Classification')} value={formData.taxClassification} />
            <FieldDisplay label={t('form.homecareExperienceYears', 'Years of Homecare Experience')} value={formData.homecareExperienceYears} />
            <FieldDisplay label={t('form.citizenStatus', 'Citizen Status')} value={formData.citizenStatus} />
            <FieldDisplay label={t('form.federalFilingStatus', 'Federal Filing Status')} value={formData.federalFilingStatus} />
            <FieldDisplay label={t('form.dependentsUnderAge17', 'Dependents Under 17')} value={formData.dependentsUnderAge17} />
            <FieldDisplay label={t('form.multipleJobs', 'Multiple Jobs')} value={formData.multipleJobs} />
          </Grid2>
        </AccordionDetails>
      </Accordion>

      {/* Emergency Contact & References */}
      <Accordion expanded={expanded === 'refs'} onChange={handleAccordion('refs')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>{t('form.emergencyContactAndReferences', 'Emergency Contact & References')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid2 container spacing={1}>
            <FieldDisplay label={t('form.emergencyContactName', 'Emergency Contact')} value={formData.emergencyContactName} />
            <FieldDisplay label={t('form.emergencyContactRelationship', 'Relationship')} value={formData.emergencyContactRelationship} />
            <FieldDisplay label={t('form.emergencyContactPhone', 'Phone')} value={formData.emergencyContactPhone} />
            <FieldDisplay label={t('form.professionalReferenceName', 'Professional Reference')} value={formData.professionalReferenceName} />
            <FieldDisplay label={t('form.professionalReferencePhone', 'Phone')} value={formData.professionalReferencePhone} />
            <FieldDisplay label={t('form.personalReferenceName', 'Personal Reference')} value={formData.personalReferenceName} />
            <FieldDisplay label={t('form.personalReferencePhone', 'Phone')} value={formData.personalReferencePhone} />
          </Grid2>
        </AccordionDetails>
      </Accordion>

      {/* Availability */}
      <Accordion expanded={expanded === 'availability'} onChange={handleAccordion('availability')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>{t('form.availabilityPreferences', 'Availability & Preferences')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid2 container spacing={1}>
            <FieldDisplay label={t('form.ableToDrive', 'Do you drive?')} value={formData.ableToDrive} />
            <FieldDisplay label={t('form.hasACar', 'Do you have a car?')} value={formData.hasACar} />
            <FieldDisplay label={t('form.worksWithPets', 'Can you work with pets?')} value={formData.worksWithPets} />
            <FieldDisplay label={t('form.worksWithSmokers', 'Can you work with smokers?')} value={formData.worksWithSmokers} />
          </Grid2>
        </AccordionDetails>
      </Accordion>

      {/* Medical */}
      <Accordion expanded={expanded === 'medical'} onChange={handleAccordion('medical')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>{t('form.medical', 'Medical')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid2 container spacing={1}>
            <FieldDisplay label={t('form.hasReceivedFluVaccineThisSeason', 'Flu Vaccine')} value={formData.hasReceivedFluVaccineThisSeason} />
            <FieldDisplay label={t('form.fluVaccineDecision', 'Flu Decision')} value={formData.fluVaccineDecision} />
            <FieldDisplay label={t('form.sysHepBDecision', 'Hep B Decision')} value={formData.sysHepBDecision} />
            <FieldDisplay label={t('form.tbSymptoms', 'TB Symptoms')} value={formData.tbSymptoms} />
            <FieldDisplay label={t('form.exposedToTb', 'Exposed to TB')} value={formData.exposedToTb} />
          </Grid2>
        </AccordionDetails>
      </Accordion>

      {/* Documents */}
      <Accordion expanded={expanded === 'documents'} onChange={handleAccordion('documents')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography fontWeight={600}>{t('form.yourDocuments', 'Your Documents')}</Typography>
            {isGeneratingDocs && <CircularProgress size={16} />}
            {!isGeneratingDocs && generatedDocs.length > 0 && (
              <Chip label={generatedDocs.length} size="small" color="primary" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {isGeneratingDocs && generatedDocs.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('form.generatingDocuments', 'Generating your documents... This may take a moment.')}
            </Typography>
          )}

          {docGenError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {docGenError}
            </Alert>
          )}

          {generatedDocs.length > 0 && (
            <List dense>
              {generatedDocs.map((doc) => (
                <ListItem key={doc.id} divider>
                  <ListItemText
                    primary={doc.documentName}
                    secondary={new Date(doc.createdAt).toLocaleDateString()}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => setViewingDoc(doc)}
                      title={t('form.viewDocument', 'View')}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          {!isGeneratingDocs && generatedDocs.length === 0 && !docGenError && (
            <Typography variant="body2" color="text.secondary">
              {t('form.noDocumentsYet', 'No documents generated yet.')}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {viewingDoc && (
        <DocumentViewerDialog
          open={true}
          onClose={() => setViewingDoc(null)}
          fetchUrl={`/api/documents/${viewingDoc.id}`}
          title={viewingDoc.documentName}
        />
      )}

      {/* Signature date only — image is stored in blob/Nexus for doc stamping */}
      {formData.portalSignatureDate && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {t('form.signedOn', 'Signed on')}: {formData.portalSignatureDate}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default SubmittedStep;
